import { watchDebounced } from '@vueuse/core';
import { computed, ref, type Ref } from 'vue';
import { useSanctumClient } from '#imports';

export type ReportingColumnOption = {
  key: string;
  label: string;
};

type UseReportingAvailableColumnsContext = {
  absencesubscribedcheckvalue: Ref<number>;
  billingvolantcheckvalue: Ref<number>;
  billingSubscribedValue: Ref<number>;
  extensionsubscriptionvalue: Ref<number>;
  cashDepositValue: Ref<number>;
  unpaidmerchantvalue: Ref<number>;
  eventvalue: Ref<number>;
  recapMerchantValue: Ref<number>;
  cityRegroupValue: Ref<number>;
  cityRegroup2Value: Ref<number>;
  maskColumnValue: Ref<number>;
};

type ReportingColumnsRequestBody = {
  type: number;
  absencesubscribedcheckvalue?: number;
  billingvolantcheckvalue?: number;
  billingSubscribedValue?: number;
  extensionsubscriptionvalue?: number;
  cashDepositValue?: number;
  unpaidmerchantvalue?: number;
  eventvalue?: number;
  recapMerchantValue?: number;
  cityRegroupValue?: number;
  cityRegroup2Value?: number;
};

export const useReportingAvailableColumns = (
  type: Ref<number | null>,
  context: UseReportingAvailableColumnsContext,
) => {
  const client = useSanctumClient();
  const columns = ref<ReportingColumnOption[]>([]);
  const pending = ref(false);
  let requestId = 0;

  const requestBody = computed<ReportingColumnsRequestBody | null>(() => {
    if (!type.value || context.maskColumnValue.value !== 1) {
      return null;
    }

    if (![1, 2, 3].includes(type.value)) {
      return null;
    }

    if (type.value === 1) {
      return {
        type: type.value,
        absencesubscribedcheckvalue: context.absencesubscribedcheckvalue.value,
        billingvolantcheckvalue: context.billingvolantcheckvalue.value,
        billingSubscribedValue: context.billingSubscribedValue.value,
        extensionsubscriptionvalue: context.extensionsubscriptionvalue.value,
        cashDepositValue: context.cashDepositValue.value,
        unpaidmerchantvalue: context.unpaidmerchantvalue.value,
        eventvalue: context.eventvalue.value,
        recapMerchantValue: context.recapMerchantValue.value,
      };
    }

    if (type.value === 3) {
      return {
        type: type.value,
        cityRegroupValue: context.cityRegroupValue.value,
        cityRegroup2Value: context.cityRegroup2Value.value,
      };
    }

    return {
      type: type.value,
    };
  });

  watchDebounced(
    requestBody,
    async (body) => {
      const currentRequestId = ++requestId;

      if (!body) {
        columns.value = [];
        pending.value = false;
        return;
      }

      pending.value = true;
      columns.value = [];

      try {
        const response = await client('/api/reportingcolumns', {
          method: 'POST',
          body: JSON.stringify(body),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });

        if (currentRequestId !== requestId) {
          return;
        }

        const rawColumns = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];

        columns.value = rawColumns
          .filter((column: any) => column?.key && column?.label)
          .map((column: any) => ({
            key: String(column.key),
            label: String(column.label),
          }));
      } catch (error) {
        if (currentRequestId !== requestId) {
          return;
        }

        console.error('Erreur chargement colonnes reporting :', error);
        columns.value = [];
      } finally {
        if (currentRequestId === requestId) {
          pending.value = false;
        }
      }
    },
    {
      debounce: 250,
      deep: true,
      immediate: true,
    },
  );

  return {
    columns,
    pending,
  };
};
